import { useCallback } from 'react';
import { Buckets } from 'src/services/file';
import { centerCameraOnPiece, updateOrbit } from 'src/utils/pattern';
import { getPiece } from 'src/utils/configurator.utils';
import { uploadImage } from 'src/utils/file.utils';
import { WebGLRenderer } from 'three';
import type { StateNames, States } from './Configurator';

// --- Utilitaires purs ---

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Capture le canvas WebGL, auto-crop le contenu non-blanc, et upload l'image.
 * Retourne le nom de fichier uploadé.
 */
function captureAndUploadImage(
  glDomElement: HTMLCanvasElement,
  bucket: Buckets,
): Promise<string> {
  return new Promise((resolve) => {
    glDomElement.toBlob(
      async (blob: Blob | null) => {
        if (!blob) {
          resolve('');
          return;
        }

        const img = new Image();
        const reader = new FileReader();

        reader.onload = () => {
          img.onload = async () => {
            const canvas = document.createElement('canvas');
            canvas.width = img.width;
            canvas.height = img.height;
            const ctx = canvas.getContext('2d');
            if (!ctx) return resolve('');

            ctx.drawImage(img, 0, 0);
            const imageData = ctx.getImageData(
              0,
              0,
              canvas.width,
              canvas.height,
            );
            const { data } = imageData;

            let top = canvas.height;
            let bottom = 0;
            let left = canvas.width;
            let right = 0;
            let foundTop = false;
            let foundBottom = false;
            let foundLeft = false;
            let foundRight = false;

            // Recherche du haut
            for (let y = 0; y < canvas.height && !foundTop; y++) {
              let lineHasColor = false;
              for (let x = 0; x < canvas.width; x++) {
                const i = (y * canvas.width + x) * 4;
                const r = data[i];
                const g = data[i + 1];
                const b = data[i + 2];
                if (r + g + b < 765) {
                  lineHasColor = true;
                  break;
                }
              }
              if (lineHasColor) {
                top = y;
                foundTop = true;
              }
            }

            // Recherche du bas
            for (let y = canvas.height - 1; y >= 0 && !foundBottom; y--) {
              let lineHasColor = false;
              for (let x = 0; x < canvas.width; x++) {
                const i = (y * canvas.width + x) * 4;
                const r = data[i];
                const g = data[i + 1];
                const b = data[i + 2];
                if (r + g + b < 765) {
                  lineHasColor = true;
                  break;
                }
              }
              if (lineHasColor) {
                bottom = y;
                foundBottom = true;
              }
            }

            // Recherche de la gauche
            for (let x = 0; x < canvas.width && !foundLeft; x++) {
              let columnHasColor = false;
              for (let y = 0; y < canvas.height; y++) {
                const i = (y * canvas.width + x) * 4;
                const r = data[i];
                const g = data[i + 1];
                const b = data[i + 2];
                if (r + g + b < 765) {
                  columnHasColor = true;
                  break;
                }
              }
              if (columnHasColor) {
                left = x;
                foundLeft = true;
              }
            }

            // Recherche de la droite
            for (let x = canvas.width - 1; x >= 0 && !foundRight; x--) {
              let columnHasColor = false;
              for (let y = 0; y < canvas.height; y++) {
                const i = (y * canvas.width + x) * 4;
                const r = data[i];
                const g = data[i + 1];
                const b = data[i + 2];
                if (r + g + b < 765) {
                  columnHasColor = true;
                  break;
                }
              }
              if (columnHasColor) {
                right = x;
                foundRight = true;
              }
            }

            const cropWidth = right - left;
            const cropHeight = bottom - top;

            const cropCanvas = document.createElement('canvas');
            cropCanvas.width = cropWidth;
            cropCanvas.height = cropHeight;
            const cropCtx = cropCanvas.getContext('2d');
            if (!cropCtx) return resolve('');

            cropCtx.drawImage(
              canvas,
              left,
              top,
              cropWidth,
              cropHeight,
              0,
              0,
              cropWidth,
              cropHeight,
            );

            cropCanvas.toBlob(
              async (croppedBlob) => {
                if (!croppedBlob) return resolve('');
                const file = new File([croppedBlob], 'image-cropped.jpeg', {
                  type: 'image/jpeg',
                });
                const pictureName = await uploadImage(file, bucket);
                resolve(`${pictureName}`);
              },
              'image/jpeg',
              0.95,
            );
          };

          img.src = reader.result as string;
        };

        reader.readAsDataURL(blob);
      },
      'image/jpeg',
      0.95,
    );
  });
}

// --- Hook ---

/**
 * Hook qui gère la prise de photos du configurateur 3D :
 * capture séquentielle de toutes les faces avec patterns,
 * auto-crop et upload.
 */
export const useTakePictures = (
  gl: WebGLRenderer | undefined,
  orbitRef: React.MutableRefObject<any>,
  pieces: any[],
  customSteles: any[],
  customPlatings: any[],
  customSoles: any[],
  patterns: {
    [key: string]: {
      [key: string]: any;
    }[];
  },
  centerCamera: (angle?: number) => void,
  setValue: <T extends StateNames>(stateName: T, value: States[T]) => void,
  setPieceIndex: (pieceIndex: number) => void,
) => {
  const takePictures = useCallback(async (): Promise<string[]> => {
    setValue('texture', true);
    setPieceIndex(-1);
    centerCamera(30);

    const result = Object.entries(patterns).flatMap(([key, subkeys]) =>
      Object.entries(subkeys).map(([subkey, values]) => {
        let pieceI = null;

        for (const [index, piece] of pieces.entries()) {
          if (piece.uuid === key) pieceI = index;
        }
        if (pieceI === null) {
          for (const [index, piece] of customSteles.entries()) {
            if (piece.id === key) pieceI = pieces.length + index;
          }
        }
        if (pieceI === null) {
          for (const [index, piece] of customPlatings.entries()) {
            if (piece.id === key)
              pieceI = pieces.length + customSteles.length + index;
          }
        }
        return {
          key,
          subkey,
          pieceIndex: pieceI,
          ...values,
        };
      }),
    );

    const images: Promise<string>[] = [];

    async function processResult() {
      if (!gl) return [];

      // Capture de la vue d'ensemble
      await centerAndCapture(0, Buckets.BUCKET_3D_PIECES);

      // Capture de chaque pattern
      for (const [resIndex, res] of result.entries()) {
        if (res.pieceIndex !== null) {
          const piece = getPiece(
            res.pieceIndex,
            pieces,
            customSteles,
            customPlatings,
            customSoles,
          );
          updateOrbit(
            gl,
            orbitRef,
            res.pieceIndex,
            pieces,
            customSteles,
            customPlatings,
            { key: res.subkey as any, inverse: res.subkey.includes('inverse') },
            centerCamera,
            setValue,
            () => {
              if (resIndex === result.length - 1 && res.pieceIndex !== null)
                centerCameraOnPiece(res.pieceIndex, piece, orbitRef);
            },
          );
        }

        await delay(1000 * resIndex + 1);

        const imageUrl = captureAndUploadImage(
          gl.domElement,
          Buckets.BUCKET_3D_PIECES,
        );
        images.push(imageUrl);
      }

      // Captures aux différents angles
      await centerAndCapture(30, Buckets.BUCKET_3D_PIECES);
      await centerAndCapture(-30, Buckets.BUCKET_3D_PIECES, () => {
        setValue('texture', false);
      });
      await centerAndCapture(30, Buckets.BUCKET_3D_PIECES);
      await centerAndCapture(-30, Buckets.BUCKET_3D_PIECES, () => {
        setPieceIndex(-1);
        centerCamera(30);
      });

      return Promise.all(images);
    }

    async function centerAndCapture(
      angle: number | null,
      bucket: Buckets,
      callback?: () => void,
    ) {
      if (!gl) return;
      if (angle !== null) centerCamera(angle);
      await delay(1000);
      const imageUrl = captureAndUploadImage(gl.domElement, bucket);
      images.push(imageUrl);
      if (callback) callback();
    }

    return processResult();
  }, [
    gl,
    orbitRef,
    pieces,
    customSteles,
    customPlatings,
    customSoles,
    patterns,
    centerCamera,
    setValue,
    setPieceIndex,
  ]);

  return { takePictures };
};
